// src/components/admin/VentasData/VentasData.jsx
import React, { useEffect, useMemo, useState } from 'react';
import './VentasData.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSync, faEye, faFileExcel, faFilePdf, faHandHoldingDollar, faTimes,
    faFilter, faTrash, faCheckCircle, faClock, faCoins, faBox, faPlus, faFilePdf as faPdf
} from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import moneda from '../../moneda';
import ventasService from '../../../services/ventasService';
import NewVenta from '../NewVenta/NewVenta';

export default function VentasData() {
    const [ventas, setVentas] = useState([]);
    const [estadisticas, setEstadisticas] = useState({});
    const [loading, setLoading] = useState(false);

    // Filtros
    const [filtroEstado, setFiltroEstado] = useState('');
    const [filtroDesde, setFiltroDesde] = useState('');
    const [filtroHasta, setFiltroHasta] = useState('');
    const [busqueda, setBusqueda] = useState('');

    // Modal detalle / seguimiento
    const [modalVisible, setModalVisible] = useState(false);
    const [venta, setVenta] = useState({});
    const [abonos, setAbonos] = useState([]);
    const [abonosLoading, setAbonosLoading] = useState(false);

    // Form abono
    const [montoAbono, setMontoAbono] = useState('');
    const [metodoAbono, setMetodoAbono] = useState('Efectivo');
    const [notaAbono, setNotaAbono] = useState('');
    const [guardandoAbono, setGuardandoAbono] = useState(false);

    // Toasts
    const [toasts, setToasts] = useState([]);
    const showToast = (message, type = 'info') => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3600);
    };

    useEffect(() => {
        cargarVentas();
    }, [filtroEstado, filtroDesde, filtroHasta]); // eslint-disable-line react-hooks/exhaustive-deps

    const cargarVentas = async () => {
        setLoading(true);
        const data = await ventasService.listar({
            estado: filtroEstado,
            desde: filtroDesde,
            hasta: filtroHasta,
        });
        setLoading(false);
        if (data.error) {
            showToast(data.error, 'error');
            setVentas([]);
            setEstadisticas({});
            return;
        }
        setVentas(data.ventas || []);
        setEstadisticas(data.estadisticas || {});
    };

    const recargar = () => {
        cargarVentas();
        showToast('Datos actualizados', 'info');
    };

    // ---------- Helpers ----------
    const formatMoney = (v) =>
        `${moneda} ${Number(v || 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const gananciaDe = (v) => Number(v.valorVenta || 0) - Number(v.valorCompra || 0);
    const abonadoDe = (v) => v.totalAbonado ?? 0;
    const saldoDe = (v) => v.saldo ?? (Number(v.valorVenta || 0) - Number(v.totalAbonado || 0));

    const estadoBadge = (estado) => {
        const e = (estado || '').toLowerCase();
        if (e === 'pagado') return 'success';
        if (e === 'abonado') return 'warning';
        if (e === 'cancelado') return 'error';
        return '';
    };

    const estadoIcon = (estado) => {
        const e = (estado || '').toLowerCase();
        if (e === 'pagado') return faCheckCircle;
        if (e === 'abonado') return faCoins;
        return faClock;
    };

    const isCancelada = (v) => (v?.estado || '').toLowerCase() === 'cancelado';
    const isPagada = (v) => (v?.estado || '').toLowerCase() === 'pagado';

    // ---------- Filtro local (búsqueda por texto) ----------
    const filtradas = useMemo(() => {
        const q = busqueda.trim().toLowerCase();
        if (!q) return ventas;
        return ventas.filter(v =>
            (v.clienteNombre || '').toLowerCase().includes(q) ||
            (v.elementoNombre || '').toLowerCase().includes(q) ||
            (v.clienteTelefono || '').toLowerCase().includes(q)
        );
    }, [ventas, busqueda]);

    const limpiarFiltros = () => {
        setFiltroEstado('');
        setFiltroDesde('');
        setFiltroHasta('');
        setBusqueda('');
    };

    // ---------- Modal ----------
    const abrirModal = async (v) => {
        setVenta(v);
        setModalVisible(true);
        setMontoAbono('');
        setNotaAbono('');
        setMetodoAbono('Efectivo');
        await cargarAbonos(v.idVenta);
    };

    const cargarAbonos = async (idVenta) => {
        setAbonosLoading(true);
        const data = await ventasService.listarAbonos(idVenta);
        setAbonosLoading(false);
        if (data.error) {
            showToast(data.error, 'warning');
            setAbonos([]);
            return;
        }
        setAbonos(data.abonos || []);
    };

    const cerrarModal = () => {
        setModalVisible(false);
        setVenta({});
        setAbonos([]);
        setMontoAbono('');
        setNotaAbono('');
    };

    const registrarAbono = async () => {
        const monto = parseFloat(montoAbono);
        if (Number.isNaN(monto) || monto <= 0) return showToast('Ingrese un monto válido', 'warning');
        if (monto > saldoDe(venta)) return showToast('El abono supera el saldo pendiente', 'warning');
        setGuardandoAbono(true);
        const data = await ventasService.crearAbono({
            idVenta: venta.idVenta,
            monto,
            metodo: metodoAbono,
            nota: notaAbono.trim(),
        });
        setGuardandoAbono(false);
        if (data.error) return showToast(data.error, 'error');
        showToast(data.mensaje || 'Abono registrado', 'success');
        // Actualizar venta con saldo/estado devueltos por el backend
        setVenta(prev => ({
            ...prev,
            saldo: data.saldo ?? saldoDe(prev) - monto,
            estado: data.estado ?? prev.estado,
            totalAbonado: (prev.totalAbonado ?? 0) + monto,
        }));
        setMontoAbono('');
        setNotaAbono('');
        await cargarAbonos(venta.idVenta);
        cargarVentas();
    };

    const eliminarAbono = async (idAbono) => {
        const { isConfirmed } = await Swal.fire({
            title: '¿Eliminar abono?',
            text: 'Se recalculará el saldo de la venta.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'No',
        });
        if (!isConfirmed) return;
        const data = await ventasService.eliminarAbono(idAbono);
        if (data.error) return showToast(data.error, 'error');
        showToast(data.mensaje || 'Abono eliminado', 'success');
        setVenta(prev => ({
            ...prev,
            saldo: data.saldo ?? prev.saldo,
            estado: data.estado ?? prev.estado,
        }));
        await cargarAbonos(venta.idVenta);
        cargarVentas();
    };

    const cambiarEstado = async (estado) => {
        const cfg = estado === 'Pagado'
            ? {
                title: '¿Marcar como pagada?',
                html: `La venta de <b>${venta.clienteNombre || ''}</b> quedará saldada. No se podrán registrar más abonos.`,
                icon: 'question',
                confirmButtonColor: '#10b981',
                confirmButtonText: 'Sí, marcar pagada',
            }
            : {
                title: '¿Cancelar esta venta?',
                html: `La venta de <b>${venta.clienteNombre || ''}</b> quedará cancelada. Podrás consultarla pero no gestionar abonos.`,
                icon: 'warning',
                confirmButtonColor: '#ef4444',
                confirmButtonText: 'Sí, cancelar',
            };
        const { isConfirmed } = await Swal.fire({
            ...cfg,
            showCancelButton: true,
            cancelButtonColor: '#6b7280',
            cancelButtonText: 'No',
        });
        if (!isConfirmed) return;
        const data = await ventasService.actualizar(venta.idVenta, { estado });
        if (data.error) return showToast(data.error, 'error');
        showToast(data.mensaje || 'Venta actualizada', 'success');
        setVenta(prev => ({ ...prev, estado }));
        cargarVentas();
    };

    const eliminarVenta = async (v) => {
        const { isConfirmed } = await Swal.fire({
            title: '¿Eliminar venta?',
            text: `Se eliminará la venta de ${v.clienteNombre} y sus abonos.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'No',
        });
        if (!isConfirmed) return;
        const data = await ventasService.eliminar(v.idVenta);
        if (data.error) return showToast(data.error, 'error');
        showToast(data.mensaje || 'Venta eliminada', 'success');
        if (venta.idVenta === v.idVenta) cerrarModal();
        cargarVentas();
    };

    // ---------- Exportaciones ----------
    const descargarExcel = () => {
        const data = filtradas.map(v => ({
            'ID': v.idVenta,
            'Cliente': v.clienteNombre,
            'Teléfono': v.clienteTelefono || '',
            'Identificación': v.clienteIdentificacion || '',
            'Elemento': v.elementoNombre,
            'Origen': v.idProducto ? 'Catálogo' : 'Libre',
            'Valor compra': Number(v.valorCompra || 0),
            'Valor venta': Number(v.valorVenta || 0),
            'Ganancia': gananciaDe(v),
            'Abonado': abonadoDe(v),
            'Saldo': saldoDe(v),
            'Estado': v.estado,
            'Fecha': v.createdAt,
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Ventas');
        XLSX.writeFile(wb, 'ventas.xlsx');
        showToast('Excel generado', 'success');
    };

    const descargarPDF = () => {
        const pdf = new jsPDF('landscape');
        pdf.text('Ventas Gestionadas', 10, 10);
        const columns = ['ID', 'Cliente', 'Elemento', 'V. Venta', 'Abonado', 'Saldo', 'Ganancia', 'Estado', 'Fecha'];
        const body = filtradas.map(v => [
            v.idVenta,
            v.clienteNombre,
            v.elementoNombre,
            formatMoney(v.valorVenta),
            formatMoney(abonadoDe(v)),
            formatMoney(saldoDe(v)),
            formatMoney(gananciaDe(v)),
            v.estado,
            v.createdAt,
        ]);
        pdf.autoTable({ head: [columns], body, styles: { fontSize: 8 }, headStyles: { fillColor: [53, 92, 125] } });
        pdf.save('ventas.pdf');
        showToast('PDF generado', 'success');
    };

    const descargarComprobante = (v) => {
        const pdf = new jsPDF('p', 'mm', 'a4');
        let y = 15;
        pdf.setFontSize(16); pdf.setFont('helvetica', 'bold');
        pdf.text('Comprobante de Venta', 10, y); y += 10;
        pdf.setFontSize(10); pdf.setFont('helvetica', 'normal');
        const detalle = [
            ['ID Venta', `${v.idVenta || ''}`],
            ['Cliente', `${v.clienteNombre || ''}`],
            ['Teléfono', `${v.clienteTelefono || ''}`],
            ['Identificación', `${v.clienteIdentificacion || ''}`],
            ['Elemento', `${v.elementoNombre || ''}`],
            ['Valor de compra', formatMoney(v.valorCompra)],
            ['Valor de venta', formatMoney(v.valorVenta)],
            ['Ganancia', formatMoney(gananciaDe(v))],
            ['Total abonado', formatMoney(abonadoDe(v))],
            ['Saldo pendiente', formatMoney(saldoDe(v))],
            ['Estado', `${v.estado || ''}`],
            ['Nota', `${v.nota || 'Sin observaciones'}`],
            ['Fecha', `${v.createdAt || ''}`],
        ];
        pdf.autoTable({
            startY: y, head: [['Campo', 'Valor']], body: detalle,
            styles: { fontSize: 9, cellPadding: 3 }, headStyles: { fillColor: [53, 92, 125] },
            columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 }, 1: { cellWidth: 125 } },
        });
        y = pdf.lastAutoTable.finalY + 10;
        if (abonos.length > 0) {
            pdf.setFontSize(12); pdf.setFont('helvetica', 'bold');
            pdf.text('Historial de abonos', 10, y); y += 4;
            pdf.autoTable({
                startY: y,
                head: [['#', 'Monto', 'Método', 'Nota', 'Fecha']],
                body: abonos.map((a, i) => [i + 1, formatMoney(a.monto), a.metodo || '—', a.nota || '', a.createdAt || '']),
                styles: { fontSize: 8, cellPadding: 2 }, headStyles: { fillColor: [53, 92, 125] },
            });
        }
        pdf.save(`venta_${v.idVenta || 'comprobante'}.pdf`);
        showToast('Comprobante generado', 'success');
    };

    // ---------- UI ----------
    return (
        <div>
            {/* Toasts */}
            {toasts.length > 0 && (
                <div className="product-toast-container">
                    {toasts.map(t => (
                        <div key={t.id} className={`product-toast ${t.type}`}>
                            <div className="toast-icon">
                                {t.type === 'success' && '✅'}
                                {t.type === 'error' && '❌'}
                                {t.type === 'warning' && '⚠️'}
                                {t.type === 'info' && 'ℹ️'}
                            </div>
                            <div className="toast-content">
                                <div className="toast-title">{t.message}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Header + stats */}
            <div className="global-component-header" style={{ padding: '12px 16px' }}>
                <div className="flex-between" style={{ width: '100%' }}>
                    <div className="flex-center" style={{ gap: '16px', flexWrap: 'wrap' }}>
                        <div className="global-component-title">
                            <FontAwesomeIcon icon={faHandHoldingDollar} />
                            Ventas Gestionadas
                        </div>
                        <div className="global-component-stats">
                            <div className="global-stat-card compact">
                                <span className="global-stat-number">{estadisticas.total_ventas ?? filtradas.length}</span>
                                <span className="global-stat-label">Ventas</span>
                            </div>
                            <div className="global-stat-card compact">
                                <span className="global-stat-number">{formatMoney(estadisticas.total_vendido)}</span>
                                <span className="global-stat-label">Vendido</span>
                            </div>
                            <div className="global-stat-card compact">
                                <span className="global-stat-number">{formatMoney(estadisticas.saldo_por_cobrar)}</span>
                                <span className="global-stat-label">Por cobrar</span>
                            </div>
                            <div className="global-stat-card compact">
                                <span className="global-stat-number">{formatMoney(estadisticas.ganancia_total)}</span>
                                <span className="global-stat-label">Ganancia</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex-center" style={{ gap: 8 }}>
                        <button className="btn-product-primary compact flex-center" onClick={descargarExcel} title="Exportar Excel">
                            <FontAwesomeIcon icon={faFileExcel} />
                        </button>
                        <button className="btn-product-primary compact flex-center" onClick={descargarPDF} title="Exportar PDF">
                            <FontAwesomeIcon icon={faFilePdf} />
                        </button>
                        <button className="btn-product-secondary compact flex-center" onClick={recargar} title="Actualizar">
                            <FontAwesomeIcon icon={faSync} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Botón nueva venta */}
            <div className="new-product-container">
                <NewVenta onCreated={cargarVentas} />
            </div>

            {/* Filtros */}
            <div className="inputsGrap" style={{ marginBottom: 16 }}>
                <div className="ventas-filtros-grid">
                    <div className="product-form-group">
                        <label className="product-form-label">Buscar</label>
                        <input className="product-form-input" placeholder="Cliente, elemento o teléfono" value={busqueda} onChange={e => setBusqueda(e.target.value)} />
                    </div>
                    <div className="product-form-group">
                        <label className="product-form-label">Estado</label>
                        <select className="product-form-input" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
                            <option value="">Todos</option>
                            <option value="Pendiente">Pendiente</option>
                            <option value="Abonado">Abonado</option>
                            <option value="Pagado">Pagado</option>
                            <option value="Cancelado">Cancelado</option>
                        </select>
                    </div>
                    <div className="product-form-group">
                        <label className="product-form-label">Desde</label>
                        <input type="date" className="product-form-input" value={filtroDesde} onChange={e => setFiltroDesde(e.target.value)} />
                    </div>
                    <div className="product-form-group">
                        <label className="product-form-label">Hasta</label>
                        <input type="date" className="product-form-input" value={filtroHasta} onChange={e => setFiltroHasta(e.target.value)} />
                    </div>
                    <div className="product-form-group" style={{ alignSelf: 'end' }}>
                        <button className="btn-product-secondary" onClick={limpiarFiltros}>
                            <FontAwesomeIcon icon={faFilter} style={{ marginRight: 8 }} /> Limpiar
                        </button>
                    </div>
                </div>
            </div>

            {/* Tabla (desktop) */}
            <div className="global-table-container desktop-only">
                {loading ? (
                    <div className="global-loading-state">
                        <FontAwesomeIcon icon={faSync} size="2x" spin />
                        <div>Cargando ventas...</div>
                    </div>
                ) : filtradas.length > 0 ? (
                    <table className="global-table">
                        <thead>
                            <tr>
                                <th>Cliente</th>
                                <th>Elemento</th>
                                <th>V. Venta</th>
                                <th>Abonado</th>
                                <th>Saldo</th>
                                <th>Ganancia</th>
                                <th>Estado</th>
                                <th>Fecha</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtradas.map((v, index) => (
                                <tr key={v.idVenta} style={{ animationDelay: `${index * 0.05}s` }}>
                                    <td>
                                        <div>
                                            <strong>{v.clienteNombre}</strong><br />
                                            <small className="text-muted">{v.clienteTelefono || 'Sin contacto'}</small>
                                        </div>
                                    </td>
                                    <td>
                                        <div>
                                            {v.elementoNombre}
                                            <br />
                                            <small className="text-muted">{v.idProducto ? 'Catálogo' : 'Libre'}</small>
                                        </div>
                                    </td>
                                    <td style={{ fontWeight: 700 }}>{formatMoney(v.valorVenta)}</td>
                                    <td>{formatMoney(abonadoDe(v))}</td>
                                    <td style={{ fontWeight: 700, color: saldoDe(v) > 0 ? '#dc2626' : '#16a34a' }}>{formatMoney(saldoDe(v))}</td>
                                    <td style={{ color: gananciaDe(v) >= 0 ? '#16a34a' : '#dc2626' }}>{formatMoney(gananciaDe(v))}</td>
                                    <td>
                                        <span className={`global-indicator ${estadoBadge(v.estado)}`}>
                                            <FontAwesomeIcon icon={estadoIcon(v.estado)} /> {v.estado || '—'}
                                        </span>
                                    </td>
                                    <td>{v.createdAt}</td>
                                    <td>
                                        <div className="global-table-actions">
                                            <button className="global-btn-edit" title="Ver / Gestionar" onClick={() => abrirModal(v)}>
                                                <FontAwesomeIcon icon={faEye} />
                                            </button>
                                            <button className="global-btn-delete" title="Eliminar" onClick={() => eliminarVenta(v)}>
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="global-empty-state">
                        <FontAwesomeIcon icon={faHandHoldingDollar} size="3x" />
                        <div className="global-empty-title">No hay ventas</div>
                        <div className="global-empty-description">Registra una nueva venta o ajusta los filtros.</div>
                    </div>
                )}
            </div>

            {/* Tarjetas (mobile) */}
            {!loading && (
                <div className="mobile-only card-list">
                    {filtradas.length === 0 ? (
                        <div className="global-empty-state">
                            <FontAwesomeIcon icon={faHandHoldingDollar} size="3x" />
                            <div className="global-empty-title">No hay ventas</div>
                            <div className="global-empty-description">Registra una nueva venta o ajusta los filtros.</div>
                        </div>
                    ) : (
                        filtradas.map((v) => (
                            <div className="product-card" key={v.idVenta}>
                                <div className="card-header">
                                    <div className="card-image-container">
                                        {v.elementoImagen ? (
                                            <img className="card-image" src={v.elementoImagen} alt={v.elementoNombre} />
                                        ) : (
                                            <div className="card-image-placeholder">
                                                <FontAwesomeIcon icon={faBox} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="card-info">
                                        <div className="card-title">{v.clienteNombre}</div>
                                        <div>
                                            <span className={`global-indicator ${estadoBadge(v.estado)}`}>
                                                <FontAwesomeIcon icon={estadoIcon(v.estado)} /> {v.estado}
                                            </span>
                                        </div>
                                        <div className="text-muted">{v.elementoNombre}</div>
                                        <div className="text-muted">Saldo: {formatMoney(saldoDe(v))}</div>
                                    </div>
                                </div>
                                <div className="card-footer">
                                    <div className="card-stock">
                                        <span className="stock-label">Venta:</span>
                                        <span className="stock-value">{formatMoney(v.valorVenta)}</span>
                                    </div>
                                    <div className="card-actions">
                                        <button className="global-btn-edit" title="Ver / Gestionar" onClick={() => abrirModal(v)}>
                                            <FontAwesomeIcon icon={faEye} />
                                        </button>
                                        <button className="global-btn-delete" title="Eliminar" onClick={() => eliminarVenta(v)}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Modal detalle / seguimiento */}
            {modalVisible && (
                <div className="product-modal-backdrop" style={{ height: '100svh' }}>
                    <div className="product-modal-container" style={{ maxWidth: 900, height: '100svh', maxHeight: '100svh', display: 'flex', flexDirection: 'column' }}>
                        <div className="product-modal-header">
                            <div className="product-modal-title">
                                <FontAwesomeIcon icon={faHandHoldingDollar} /> Venta #{venta.idVenta} — {venta.clienteNombre}
                            </div>
                            <button className="product-close-button" onClick={cerrarModal}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div className="product-modal-body" style={{ flex: 1, maxHeight: 'calc(100svh - 160px)', overflowY: 'auto' }}>
                            {/* Resumen */}
                            <div className="product-section-card mb-24">
                                <div className="product-section-header">
                                    <div className="product-section-title">Información de la Venta</div>
                                    <span className={`global-indicator ${estadoBadge(venta.estado)}`}>
                                        <FontAwesomeIcon icon={estadoIcon(venta.estado)} /> {venta.estado}
                                    </span>
                                </div>
                                <div className="product-section-content">
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                                        {[
                                            ['Cliente', venta.clienteNombre],
                                            ['Teléfono', venta.clienteTelefono || '—'],
                                            ['Identificación', venta.clienteIdentificacion || '—'],
                                            ['Elemento', venta.elementoNombre],
                                            ['Origen', venta.idProducto ? 'Catálogo' : 'Libre'],
                                            ['Valor de compra', formatMoney(venta.valorCompra)],
                                            ['Valor de venta', formatMoney(venta.valorVenta)],
                                            ['Ganancia', formatMoney(gananciaDe(venta))],
                                            ['Total abonado', formatMoney(abonadoDe(venta))],
                                            ['Saldo pendiente', formatMoney(saldoDe(venta))],
                                            ['Nota', venta.nota || 'Sin observaciones'],
                                            ['Fecha', venta.createdAt],
                                        ].map(([label, value]) => (
                                            <div className="product-form-group" key={label}>
                                                <label className="product-form-label">{label}</label>
                                                <input className="product-form-input" value={value ?? ''} disabled
                                                    style={label === 'Saldo pendiente' ? { fontWeight: 'bold', color: saldoDe(venta) > 0 ? '#dc2626' : '#16a34a' } : {}} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Registrar abono */}
                            {!isCancelada(venta) && !isPagada(venta) && (
                                <div className="product-section-card mb-24">
                                    <div className="product-section-header">
                                        <div className="product-section-title"><FontAwesomeIcon icon={faCoins} /> Registrar Abono</div>
                                    </div>
                                    <div className="product-section-content">
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, alignItems: 'end' }}>
                                            <div className="product-form-group">
                                                <label className="product-form-label">Monto</label>
                                                <input type="number" min="0" step="0.01" className="product-form-input" value={montoAbono} onChange={e => setMontoAbono(e.target.value)} placeholder={`Máx ${formatMoney(saldoDe(venta))}`} />
                                            </div>
                                            <div className="product-form-group">
                                                <label className="product-form-label">Método</label>
                                                <select className="product-form-input" value={metodoAbono} onChange={e => setMetodoAbono(e.target.value)}>
                                                    <option>Efectivo</option>
                                                    <option>Transferencia</option>
                                                    <option>Tarjeta</option>
                                                    <option>Otro</option>
                                                </select>
                                            </div>
                                            <div className="product-form-group">
                                                <label className="product-form-label">Nota</label>
                                                <input className="product-form-input" value={notaAbono} onChange={e => setNotaAbono(e.target.value)} maxLength={200} />
                                            </div>
                                            <div className="product-form-group">
                                                <button className="btn-product-primary flex-center" style={{ gap: 6 }} onClick={registrarAbono} disabled={guardandoAbono}>
                                                    <FontAwesomeIcon icon={faPlus} /> {guardandoAbono ? 'Guardando...' : 'Abonar'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Historial de abonos */}
                            <div className="product-section-card">
                                <div className="product-section-header">
                                    <div className="product-section-title">Historial de Abonos ({abonos.length})</div>
                                </div>
                                <div className="product-section-content">
                                    {abonosLoading ? (
                                        <div className="global-loading-state"><div className="loading-spinner"></div> Cargando abonos...</div>
                                    ) : abonos.length > 0 ? (
                                        <div className="global-table-container">
                                            <table className="global-table">
                                                <thead>
                                                    <tr><th>#</th><th>Monto</th><th>Método</th><th>Nota</th><th>Fecha</th><th></th></tr>
                                                </thead>
                                                <tbody>
                                                    {abonos.map((a, i) => (
                                                        <tr key={a.idAbono}>
                                                            <td>{i + 1}</td>
                                                            <td style={{ fontWeight: 700 }}>{formatMoney(a.monto)}</td>
                                                            <td>{a.metodo || '—'}</td>
                                                            <td>{a.nota || '—'}</td>
                                                            <td>{a.createdAt}</td>
                                                            <td>
                                                                <button className="global-btn-delete" title="Eliminar abono" onClick={() => eliminarAbono(a.idAbono)}>
                                                                    <FontAwesomeIcon icon={faTrash} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="global-empty-state" style={{ width: '100%' }}>
                                            <FontAwesomeIcon icon={faCoins} size="2x" />
                                            <div className="global-empty-title">Sin abonos</div>
                                            <div className="global-empty-description">Aún no se registran pagos para esta venta.</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Footer acciones */}
                        <div className="product-submit-section">
                            <button className="btn-product-secondary" onClick={() => descargarComprobante(venta)}>
                                <FontAwesomeIcon icon={faPdf} style={{ marginRight: 6 }} /> Comprobante PDF
                            </button>
                            {!isCancelada(venta) && !isPagada(venta) && (
                                <button className="btn-product-secondary" style={{ background: '#10b981' }} onClick={() => cambiarEstado('Pagado')}>
                                    <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: 6 }} /> Marcar Pagado
                                </button>
                            )}
                            {!isCancelada(venta) && (
                                <button className="btn-product-secondary" style={{ background: '#ef4444' }} onClick={() => cambiarEstado('Cancelado')}>
                                    <FontAwesomeIcon icon={faTimes} style={{ marginRight: 6 }} /> Cancelar Venta
                                </button>
                            )}
                            <button className="btn-product-primary" onClick={cerrarModal}>Cerrar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
